import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPlatformConfig, platformKeys } from '@/api/platform';
import type {
  CountryRule,
  Feature,
  FeatureKey,
  MaintenanceScope,
  PlatformConfig,
} from '@/types/platform';
import { BrandSplash } from '@/components/system/BrandSplash';
import { ErrorState } from '@/components/system/ErrorState';
import { env } from '@/config/env';

export interface PlatformContextValue {
  config: PlatformConfig;
  /** Resolved feature, or undefined if the backend does not know the key. */
  feature: (key: FeatureKey) => Feature | undefined;
  /** True only when the feature is fully enabled. */
  isEnabled: (key: FeatureKey) => boolean;
  isComingSoon: (key: FeatureKey) => boolean;
  /** Whether the feature should appear in navigation at all. */
  isVisible: (key: FeatureKey) => boolean;
  inMaintenance: (scope: MaintenanceScope) => boolean;
  /**
   * True while the pre-launch holding page is showing — §24, §67.
   *
   * Read by the router (which destination to render) and by the header,
   * footer and mobile nav (whether their links are controls or decoration).
   */
  isPreLaunch: boolean;
  /** Every destination we deliver to, with its address rules (§43). */
  countries: CountryRule[];
}

/** Exported so tests can supply a context value without a live API. */
export const PlatformContext = createContext<PlatformContextValue | null>(null);

/**
 * Writes the admin-configured palette onto the document root as CSS custom
 * properties — BUILD-PLAN §75 "changing the brand configuration should update
 * the interface through design tokens".
 *
 * This is why components must reference tokens rather than literal colours:
 * everything downstream repaints from these four assignments.
 */
function applyBrandTokens(config: PlatformConfig): void {
  const root = document.documentElement;
  const { tokens } = config.brand;

  root.style.setProperty('--brand-primary', tokens.primary);
  root.style.setProperty('--brand-background', tokens.background);
  root.style.setProperty('--brand-surface', tokens.surface);
  root.style.setProperty('--brand-text', tokens.text);
}

/**
 * Loads platform configuration once and makes it available application-wide.
 *
 * Routing does not render until this resolves, because the router cannot know
 * which routes exist or which navigation entries are valid until the feature
 * flags are known. Rendering first and correcting afterwards would briefly
 * expose links to disabled features — precisely what §23 forbids.
 */
export function PlatformProvider({ children }: { children: ReactNode }) {
  const {
    data: config,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: platformKeys.bootstrap,
    queryFn: fetchPlatformConfig,
    // Platform config changes rarely and is needed by every screen; refetching
    // it on each mount would add a request to every navigation for no gain.
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  useEffect(() => {
    if (config) {
      applyBrandTokens(config);
    }
  }, [config]);

  if (isPending) {
    return <BrandSplash />;
  }

  /*
   * Without configuration the app cannot safely decide what to show, so it
   * fails to a branded error rather than guessing. Defaulting to "everything
   * enabled" here would expose unfinished modules whenever the API blipped.
   */
  if (isError || !config) {
    return (
      <ErrorState
        title="Just a moment"
        /*
         * Two audiences, and only one of them should ever see infrastructure.
         *
         * The developer message names the host, the framework and the command
         * to start it — useful at a terminal, and a gift to anyone probing the
         * stack in production. It is gated on DEV so the bundler strips it from
         * the production build entirely; `bundle-safety.test.ts` asserts that
         * it really is absent rather than trusting the gate.
         *
         * What a customer gets says only what they can act on: try again, or
         * come back shortly. No host, no stack, no blame.
         */
        description={
          import.meta.env.DEV
            ? `Could not reach the API at ${env.apiUrl}. Is the Laravel server running? Start both with "npm run dev" from the project root.`
            : 'We could not load the collection just now. Please try again in a moment.'
        }
        onRetry={() => void refetch()}
        fullScreen
      />
    );
  }

  const value: PlatformContextValue = {
    config,
    feature: (key) => config.features[key],
    // Unknown keys resolve to false, mirroring the backend's fail-closed rule.
    isEnabled: (key) => config.features[key]?.state === 'enabled',
    isComingSoon: (key) => config.features[key]?.state === 'coming_soon',
    isVisible: (key) => {
      const state = config.features[key]?.state;

      return state === 'enabled' || state === 'coming_soon';
    },
    inMaintenance: (scope) => config.maintenance[scope] === true,
    isPreLaunch: config.comingSoon === true,
    // Defaulted: an older backend without the registry must not crash the
    // form, it should simply offer no pre-checked address rules.
    countries: config.countries ?? [],
  };

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}

export function usePlatform(): PlatformContextValue {
  const context = useContext(PlatformContext);

  if (!context) {
    throw new Error('usePlatform must be used within a PlatformProvider.');
  }

  return context;
}

/** Convenience accessor for the brand block. */
export function useBrand() {
  return usePlatform().config.brand;
}
